import { Controller } from "@hotwired/stimulus"
import { showToast } from "./toast"
import { showAiOverlay, hideAiOverlay } from "./ai_overlay"

export default class extends Controller {
  static targets = [
    "button", "spinner", "buttonText",
    // Author fields
    "name", "biography", "birthDate", "deathDate", "nationality", "wikipediaUrl", "websiteUrl", "photoUrl", "photoContainer",
    // Book fields
    "title", "authorName", "description", "publicationDate", "pageCount", "language", "publisher",
    // Publisher fields
    "publisherName", "publisherLocation", "publisherWebsiteUrl", "publisherNotes", "publisherLogo", "publisherLogoContainer"
  ]

  static values = {
    url: String,
    identifyUrl: String,
    searchImagesUrl: String,
    uploadImageUrl: String,
    type: String // "author", "book", or "publisher"
  }

  get disambiguationController() {
    const el = document.getElementById("ai-disambiguation-dialog")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "ai-disambiguation")
  }

  get photoPickerController() {
    const el = document.getElementById("photo-picker-dialog")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "photo-picker")
  }

  async generate(event) {
    event.preventDefault()

    if (this.typeValue === "author") {
      await this.generateAuthorData()
    } else if (this.typeValue === "book") {
      await this.generateBookData()
    } else if (this.typeValue === "publisher") {
      await this.generatePublisherData()
    }
  }

  async generateAuthorData() {
    const name = this.hasNameTarget ? this.nameTarget.value : ""

    if (!name.trim()) {
      showToast("Please enter an author name first")
      return
    }

    this.setLoading(true)

    try {
      let photoName = name

      if (this.hasIdentifyUrlValue && this.identifyUrlValue) {
        const identified = await this.identifyEntity(this.identifyUrlValue, { name })

        if (identified === null) return // cancelled or error handled

        if (identified.skip_disambiguation) {
          if (identified.data.canonical_name && this.hasNameTarget) {
            this.nameTarget.value = identified.data.canonical_name
            photoName = identified.data.canonical_name
          }
          this.fillAuthorFields(identified.data)
          await this.searchAndPickPhoto(photoName, "photoUrl")
          return
        }

        // Show disambiguation modal
        const disambiguation = this.disambiguationController
        if (!disambiguation) {
          await this.fallbackGenerateAuthor(name)
          await this.searchAndPickPhoto(name, "photoUrl")
          return
        }

        const selectedIndex = await disambiguation.show(identified.candidates, "author")
        if (!this.element.isConnected) return

        if (selectedIndex === null) return // user cancelled

        const selected = identified.candidates[selectedIndex]
        const canonicalName = selected.name || name
        photoName = canonicalName

        if (this.hasNameTarget) {
          this.nameTarget.value = canonicalName
        }

        await this.fallbackGenerateAuthor(canonicalName)
        await this.searchAndPickPhoto(photoName, "photoUrl")
      } else {
        await this.fallbackGenerateAuthor(name)
        await this.searchAndPickPhoto(name, "photoUrl")
      }
    } catch (error) {
      console.error("AI generation error:", error)
      showToast("An error occurred while generating data")
    } finally {
      this.setLoading(false)
    }
  }

  async fallbackGenerateAuthor(name) {
    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ name })
    })

    const data = await response.json()

    if (response.ok) {
      this.fillAuthorFields(data)
    } else if (response.status === 402 && data.redirect_url) {
      window.open(data.redirect_url, "_blank")
    } else {
      showToast(data.error || "Failed to generate data")
    }
  }

  async generateBookData() {
    const title = this.hasTitleTarget ? this.titleTarget.value : ""
    const authorName = this.hasAuthorNameTarget ? this.authorNameTarget.value : ""

    if (!title.trim()) {
      showToast("Please enter a book title first")
      return
    }

    this.setLoading(true)

    try {
      if (this.hasIdentifyUrlValue && this.identifyUrlValue) {
        const identified = await this.identifyEntity(this.identifyUrlValue, { title, author_name: authorName })

        if (identified === null) return

        if (identified.skip_disambiguation) {
          this.fillBookFields(identified.data)
          return
        }

        const disambiguation = this.disambiguationController
        if (!disambiguation) {
          await this.fallbackGenerateBook(title, authorName)
          return
        }

        const selectedIndex = await disambiguation.show(identified.candidates, "book")
        if (!this.element.isConnected) return

        if (selectedIndex === null) return

        const selected = identified.candidates[selectedIndex]
        await this.fallbackGenerateBook(selected.title || title, selected.author || authorName)
      } else {
        await this.fallbackGenerateBook(title, authorName)
      }
    } catch (error) {
      console.error("AI generation error:", error)
      showToast("An error occurred while generating data")
    } finally {
      this.setLoading(false)
    }
  }

  async fallbackGenerateBook(title, authorName) {
    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ title, author_name: authorName })
    })

    const data = await response.json()

    if (response.ok) {
      this.fillBookFields(data)
    } else if (response.status === 402 && data.redirect_url) {
      window.open(data.redirect_url, "_blank")
    } else {
      showToast(data.error || "Failed to generate data")
    }
  }

  async generatePublisherData() {
    const name = this.hasPublisherNameTarget ? this.publisherNameTarget.value : ""

    if (!name.trim()) {
      showToast("Please enter a publisher name first")
      return
    }

    this.setLoading(true)

    try {
      let photoName = name

      if (this.hasIdentifyUrlValue && this.identifyUrlValue) {
        const identified = await this.identifyEntity(this.identifyUrlValue, { name })

        if (identified === null) return

        if (identified.skip_disambiguation) {
          if (identified.data.canonical_name && this.hasPublisherNameTarget) {
            this.publisherNameTarget.value = identified.data.canonical_name
            photoName = identified.data.canonical_name
          }
          this.fillPublisherFields(identified.data)
          await this.searchAndPickPhoto(photoName, "publisherLogo")
          return
        }

        const disambiguation = this.disambiguationController
        if (!disambiguation) {
          await this.fallbackGeneratePublisher(name)
          await this.searchAndPickPhoto(name, "publisherLogo")
          return
        }

        const selectedIndex = await disambiguation.show(identified.candidates, "publisher")
        if (!this.element.isConnected) return

        if (selectedIndex === null) return

        const selected = identified.candidates[selectedIndex]
        const canonicalName = selected.name || name
        photoName = canonicalName

        if (this.hasPublisherNameTarget) {
          this.publisherNameTarget.value = canonicalName
        }

        await this.fallbackGeneratePublisher(canonicalName)
        await this.searchAndPickPhoto(photoName, "publisherLogo")
      } else {
        await this.fallbackGeneratePublisher(name)
        await this.searchAndPickPhoto(name, "publisherLogo")
      }
    } catch (error) {
      console.error("AI generation error:", error)
      showToast("An error occurred while generating data")
    } finally {
      this.setLoading(false)
    }
  }

  async fallbackGeneratePublisher(name) {
    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({ name })
    })

    const data = await response.json()

    if (response.ok) {
      this.fillPublisherFields(data)
    } else if (response.status === 402 && data.redirect_url) {
      window.open(data.redirect_url, "_blank")
    } else {
      showToast(data.error || "Failed to generate data")
    }
  }

  async identifyEntity(url, params) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(params)
      })

      if (response.status === 402) {
        const data = await response.json()
        if (data.redirect_url) window.open(data.redirect_url, "_blank")
        return null
      }

      if (!response.ok) {
        // Fall back to direct generation on identify failure
        showToast("Disambiguation unavailable, generating directly...", "notice")
        return null
      }

      const data = await response.json()

      if (!data.skip_disambiguation && (!data.candidates || data.candidates.length === 0)) {
        const entityType = params.name ? "match" : "book"
        showToast(`No matching ${entityType} found`)
        return null
      }

      return data
    } catch (error) {
      console.error("Identify error:", error)
      // Graceful degradation: fall back to direct generation
      return null
    }
  }

  fillAuthorFields(data) {
    if (this.hasBiographyTarget && !this.biographyTarget.value && data.biography) {
      this.biographyTarget.value = data.biography
    }

    if (this.hasBirthDateTarget && !this.birthDateTarget.value && data.birth_date) {
      this.birthDateTarget.value = data.birth_date
    }

    if (this.hasDeathDateTarget && !this.deathDateTarget.value && data.death_date) {
      this.deathDateTarget.value = data.death_date
    }

    if (this.hasNationalityTarget && !this.nationalityTarget.value && data.nationality) {
      this.nationalityTarget.value = data.nationality
    }

    if (this.hasWikipediaUrlTarget && !this.wikipediaUrlTarget.value && data.wikipedia_url) {
      this.wikipediaUrlTarget.value = data.wikipedia_url
    }

    if (this.hasWebsiteUrlTarget && !this.websiteUrlTarget.value && data.website_url) {
      this.websiteUrlTarget.value = data.website_url
    }

    // Photo is handled by the photo picker flow, not auto-filled from AI data
  }

  updatePhotoPreview(url) {
    if (!this.hasPhotoContainerTarget) return

    const container = this.photoContainerTarget
    const imageUpload = container.querySelector('[data-controller="image-upload"]')
    if (!imageUpload) return

    const preview = imageUpload.querySelector('[data-image-upload-target="preview"]')
    const placeholder = imageUpload.querySelector('[data-image-upload-target="placeholder"]')
    const removeBtn = imageUpload.querySelector('[data-image-upload-target="removeBtn"]')
    const previewContainer = imageUpload.querySelector('[data-image-upload-target="previewContainer"]')

    const existing = imageUpload.querySelector('.ai-photo-status')
    if (existing) existing.remove()

    if (url) {
      if (preview) {
        preview.src = url
        preview.classList.remove("hidden")
        preview.style.display = ""
      }
      if (placeholder) placeholder.classList.add("hidden")
      if (removeBtn) removeBtn.classList.remove("hidden")
      if (previewContainer) previewContainer.classList.add("image-upload-preview--has-image")
    } else {
      const status = document.createElement("p")
      status.className = "ai-photo-status text-xs text-beige-500 mt-1 font-fira"
      status.textContent = "No photo found"
      imageUpload.querySelector('.image-upload-actions').appendChild(status)
    }
  }

  fillBookFields(data) {
    if (this.hasDescriptionTarget && !this.descriptionTarget.value && data.description) {
      this.descriptionTarget.value = data.description
    }

    if (this.hasPublicationDateTarget && !this.publicationDateTarget.value && data.publication_date) {
      this.publicationDateTarget.value = data.publication_date
    }

    if (this.hasPageCountTarget && !this.pageCountTarget.value && data.page_count) {
      this.pageCountTarget.value = data.page_count
    }

    if (this.hasLanguageTarget && !this.languageTarget.value && data.language) {
      this.languageTarget.value = data.language
    }

    if (this.hasPublisherTarget && !this.publisherTarget.value && data.publisher) {
      this.publisherTarget.value = data.publisher
    }
  }

  fillPublisherFields(data) {
    if (this.hasPublisherLocationTarget && !this.publisherLocationTarget.value && data.location) {
      this.publisherLocationTarget.value = data.location
    }

    if (this.hasPublisherWebsiteUrlTarget && !this.publisherWebsiteUrlTarget.value && data.website_url) {
      this.publisherWebsiteUrlTarget.value = data.website_url
    }

    if (this.hasPublisherNotesTarget && !this.publisherNotesTarget.value && data.notes) {
      this.publisherNotesTarget.value = data.notes
    }
  }

  async searchAndPickPhoto(name, targetType) {
    if (!this.hasSearchImagesUrlValue || !this.searchImagesUrlValue) return

    try {
      const response = await fetch(this.searchImagesUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ name })
      })

      if (!response.ok) return

      const data = await response.json()
      const images = data.images || []

      if (images.length === 0) {
        showToast("No photos found", "notice")
        return
      }

      const picker = this.photoPickerController
      if (!picker) return

      const selectedIndex = await picker.show(images, name)
      if (!this.element.isConnected) return
      if (selectedIndex === null) return

      const selected = images[selectedIndex]
      let finalUrl = selected.url

      if (this.hasUploadImageUrlValue && this.uploadImageUrlValue) {
        const uploadResp = await fetch(this.uploadImageUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({ url: selected.url })
        })

        if (uploadResp.ok) {
          const uploadData = await uploadResp.json()
          finalUrl = uploadData.url || finalUrl
        }
      }

      if (targetType === "photoUrl") {
        if (this.hasPhotoUrlTarget) {
          this.photoUrlTarget.value = finalUrl
          this.updatePhotoPreview(finalUrl)
        }
      } else if (targetType === "publisherLogo") {
        if (this.hasPublisherLogoTarget) {
          this.publisherLogoTarget.value = finalUrl
          this.updatePublisherLogoPreview(finalUrl)
        }
      }
    } catch (error) {
      console.error("Photo search error:", error)
    }
  }

  updatePublisherLogoPreview(url) {
    if (!this.hasPublisherLogoContainerTarget) return

    const container = this.publisherLogoContainerTarget
    const imageUpload = container.querySelector('[data-controller="image-upload"]')
    if (!imageUpload) return

    const preview = imageUpload.querySelector('[data-image-upload-target="preview"]')
    const placeholder = imageUpload.querySelector('[data-image-upload-target="placeholder"]')
    const removeBtn = imageUpload.querySelector('[data-image-upload-target="removeBtn"]')
    const previewContainer = imageUpload.querySelector('[data-image-upload-target="previewContainer"]')

    if (url) {
      if (preview) {
        preview.src = url
        preview.classList.remove("hidden")
        preview.style.display = ""
      }
      if (placeholder) placeholder.classList.add("hidden")
      if (removeBtn) removeBtn.classList.remove("hidden")
      if (previewContainer) previewContainer.classList.add("image-upload-preview--has-image")
    }
  }

  setLoading(isLoading) {
    if (isLoading) {
      showAiOverlay("AI Generating...")
    } else {
      hideAiOverlay()
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = isLoading
    }

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", !isLoading)
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = isLoading ? "Generating..." : "AI Generate"
    }
  }
}
